import { normalizeStoreTypeValue } from '../constants/storeTypes';
import {
  partConditionSelectValue,
  deriveReceivingLineCondition,
  receivingUnitDetailCount
} from '../constants/partUnitConditions';
import { normalizeInventoryCategory } from '../constants/inventoryCategories';

export function emptyUnitDetailRow(storeType = '') {
  return {
    condition: 'SERVICEABLE',
    batchNumber: '',
    serialNumber: '',
    expiryDate: '',
    storeType: normalizeStoreTypeValue(storeType) || ''
  };
}

/** Copy line-level store type onto unit rows (fill blanks, or overwrite all). */
export function withLineStoreTypeOnUnits(unitDetails, storeType, { overwrite = false } = {}) {
  const lineStore = normalizeStoreTypeValue(storeType) || '';
  return (Array.isArray(unitDetails) ? unitDetails : []).map((unit) => {
    const existing = normalizeStoreTypeValue(unit?.storeType) || '';
    if (!overwrite && existing) {
      return { ...unit, storeType: existing };
    }
    return { ...unit, storeType: lineStore };
  });
}

export function toDateInputValue(raw) {
  if (!raw) return '';
  if (typeof raw === 'string') return raw.slice(0, 10);
  const d = raw instanceof Date ? raw : new Date(raw);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

/** Client-side marker for shelf life / expiry marked as not applicable. */
export const SHELF_LIFE_NA_VALUE = 'N/A';

export function isShelfLifeNaValue(value) {
  return String(value || '').trim().toUpperCase() === 'N/A';
}

/** Normalize warehouse / shelf / bin from a receiving or inventory line. */
export function normalizeReceivingLocation(location = {}) {
  const src = location && typeof location === 'object' ? location : {};
  return {
    warehouse: String(src.warehouse || '').trim(),
    shelf: String(src.shelf || '').trim(),
    bin: String(src.bin || '').trim()
  };
}

/** Display string for structured location (warehouse / shelf / bin). */
export function formatReceivingLocation(location) {
  const normalized = normalizeReceivingLocation(location);
  return [normalized.warehouse, normalized.shelf, normalized.bin].filter(Boolean).join(' / ');
}

/** True when any warehouse / shelf / bin value is present. */
export function hasReceivingLocation(location) {
  const normalized = normalizeReceivingLocation(location);
  return Boolean(normalized.warehouse || normalized.shelf || normalized.bin);
}

/** Preserve order while dropping blanks and duplicates. */
export function uniqueNonEmptyStrings(values = []) {
  const seen = new Set();
  const out = [];
  for (const raw of values) {
    const s = String(raw || '').trim();
    if (!s || seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out;
}

/** Line-level serial storage/display: unique values joined (avoids "N/A, N/A, …" × qty). */
export function formatLineSerialNumber(serials = []) {
  return uniqueNonEmptyStrings(serials).join(', ');
}

/** Collapse a comma-joined serial string that may contain duplicates. */
export function normalizeLineSerialNumberDisplay(value) {
  if (value == null) return '';
  const s = String(value).trim();
  if (!s) return '';
  if (!s.includes(',')) return s;
  return formatLineSerialNumber(s.split(','));
}

/**
 * Prefer unique serials from unit rows / serialNumbers array;
 * fall back to collapsing the line-level serial string.
 */
export function resolveLineSerialNumber({ serialNumber, unitDetails, serialNumbers } = {}) {
  const fromUnits = Array.isArray(unitDetails)
    ? unitDetails.map((unit) => unit?.serialNumber)
    : [];
  const fromArray = Array.isArray(serialNumbers) ? serialNumbers : [];
  const fromDetails = formatLineSerialNumber([...fromUnits, ...fromArray]);
  if (fromDetails) return fromDetails;
  return normalizeLineSerialNumberDisplay(serialNumber);
}

/** Batch from line field or first populated unit-detail row. */
export function deriveLineBatchNumber(line, unitDetails) {
  const lineBatch = String(line?.batchNumber || '').trim();
  if (lineBatch) return lineBatch;
  const fromUnits = uniqueNonEmptyStrings(
    (unitDetails || line?.unitDetails || []).map((unit) => unit?.batchNumber)
  );
  if (fromUnits.length === 0) return '';
  return fromUnits.length === 1 ? fromUnits[0] : fromUnits[0];
}

/**
 * Rebuild serialNumbers / serialNumber / batchNumber from unitDetails so Review
 * and Continue draft show the same values that were entered at receive time.
 */
export function hydrateReceivingLineForForm(line = {}) {
  const shelfLifeNotApplicable = Boolean(line.shelfLifeNotApplicable);
  let unitDetails = Array.isArray(line.unitDetails)
    ? line.unitDetails.map((unit) => ({
      ...unit,
      expiryDate: normalizeUnitExpiryForForm(unit?.expiryDate, shelfLifeNotApplicable)
    }))
    : [];
  const qty = Math.max(0, Number(line.receivedQuantity) || 0);
  const unitCount = receivingUnitDetailCount(qty);
  const lineCondition = partConditionSelectValue(line.condition);
  const lineSerials = uniqueNonEmptyStrings([
    ...(Array.isArray(line.serialNumbers) ? line.serialNumbers : []),
    ...(String(line.serialNumber || '').includes(',')
      ? String(line.serialNumber).split(',')
      : [line.serialNumber])
  ]);

  // Recover unit rows when qty exists but unitDetails were stripped / never saved.
  if (unitDetails.length === 0 && unitCount > 0) {
    const lineBatch = String(line.batchNumber || '').trim();
    const lineExpiry = shelfLifeNotApplicable
      ? ''
      : (line.shelfLifeUpto ? toDateInputValue(line.shelfLifeUpto) : '');
    unitDetails = Array.from({ length: unitCount }, (_, index) => ({
      condition: lineCondition,
      batchNumber: lineBatch,
      serialNumber: lineSerials[index] || (lineSerials.length === 1 ? lineSerials[0] : ''),
      expiryDate: lineExpiry,
      storeType: normalizeStoreTypeValue(line.storeType) || ''
    }));
  } else {
    unitDetails = withLineStoreTypeOnUnits(unitDetails, line.storeType);
  }

  const serialNumbers = uniqueNonEmptyStrings([
    ...unitDetails.map((unit) => unit?.serialNumber),
    ...lineSerials
  ]);
  const serialNumber = resolveLineSerialNumber({
    ...line,
    unitDetails,
    serialNumbers
  });
  const batchNumber = deriveLineBatchNumber(line, unitDetails);

  const resolvedAircraftId = line.aircraftId?._id
    ? String(line.aircraftId._id)
    : (line.aircraftId != null && typeof line.aircraftId !== 'object'
      ? String(line.aircraftId)
      : '');

  return {
    ...line,
    quantity: Number(line.quantity) || 0,
    receivedQuantity: qty,
    aircraftId: resolvedAircraftId || undefined,
    aircraftRegistration: line.aircraftId?.registration || line.aircraftRegistration || '',
    storeType: normalizeStoreTypeValue(line.storeType) || '',
    locationText: String(line.locationText || '').trim(),
    location: normalizeReceivingLocation(line.location),
    shelfLifeNotApplicable,
    shelfLifeUpto: shelfLifeNotApplicable
      ? ''
      : (line.shelfLifeUpto ? toDateInputValue(line.shelfLifeUpto) : ''),
    unitDetails,
    serialNumbers,
    serialNumber: serialNumber || '',
    batchNumber: batchNumber || ''
  };
}

/**
 * Push line-level serial/batch edits into unitDetails so approve payload
 * does not prefer stale unit rows over the inspector's table edits.
 */
export function syncLineSerialBatchIntoUnitDetails(line = {}) {
  const condition = partConditionSelectValue(line.condition);
  const qty = Math.max(0, Number(line.receivedQuantity) || 0);
  const serials = uniqueNonEmptyStrings(
    String(line.serialNumber || '').includes(',')
      ? String(line.serialNumber).split(',')
      : (Array.isArray(line.serialNumbers) && line.serialNumbers.length > 0
        ? line.serialNumbers
        : [line.serialNumber])
  );
  const unitCount = Math.max(
    receivingUnitDetailCount(qty),
    serials.length,
    Array.isArray(line.unitDetails) ? line.unitDetails.length : 0
  );
  if (unitCount <= 0) {
    return {
      ...line,
      unitDetails: [],
      serialNumbers: serials,
      serialNumber: formatLineSerialNumber(serials),
      batchNumber: String(line.batchNumber || '').trim()
    };
  }
  const currentRows = Array.isArray(line.unitDetails) ? line.unitDetails : [];
  const lineBatch = String(line.batchNumber || '').trim();
  const lineStore = normalizeStoreTypeValue(line.storeType) || '';
  const existingBatches = uniqueNonEmptyStrings(currentRows.map((unit) => unit?.batchNumber));
  const propagateBatch = Boolean(lineBatch) && existingBatches.length <= 1;
  const unitDetails = Array.from({ length: unitCount }, (_, index) => {
    const prior = currentRows[index] || { condition, batchNumber: '', serialNumber: '', storeType: lineStore };
    return {
      ...prior,
      condition: partConditionSelectValue(prior.condition || condition),
      storeType: normalizeStoreTypeValue(prior.storeType) || lineStore,
      // Line-level serial edit fully replaces unit serials from the parsed list.
      serialNumber: serials.length > 0
        ? (index < serials.length ? serials[index] : '')
        : '',
      batchNumber: propagateBatch
        ? lineBatch
        : (prior.batchNumber || lineBatch || '')
    };
  });
  return {
    ...line,
    unitDetails,
    serialNumbers: serials,
    serialNumber: formatLineSerialNumber(serials) || String(line.serialNumber || '').trim(),
    batchNumber: lineBatch || deriveLineBatchNumber(line, unitDetails)
  };
}

/** Normalize a unit expiry for form/dialog display (keeps N/A editable). */
export function normalizeUnitExpiryForForm(value, shelfLifeNotApplicable = false) {
  if (isShelfLifeNaValue(value)) return SHELF_LIFE_NA_VALUE;
  const dateVal = toDateInputValue(value);
  if (dateVal) return dateVal;
  return shelfLifeNotApplicable ? SHELF_LIFE_NA_VALUE : '';
}

/** Persistable expiry from a unit form value — N/A / blank becomes empty. */
export function unitExpiryForPersistence(value) {
  if (!value || isShelfLifeNaValue(value)) return '';
  return toDateInputValue(value) || String(value).trim();
}

/** Strip N/A / invalid expiry from unit rows before API submit (issuance, returns, etc.). */
export function sanitizeUnitDetailsForApiSubmit(unitDetails) {
  if (!Array.isArray(unitDetails)) return [];
  return unitDetails.map((unit) => {
    const row = { ...unit };
    const expiry = unitExpiryForPersistence(row.expiryDate);
    if (expiry) row.expiryDate = expiry;
    else delete row.expiryDate;
    return row;
  });
}

export function createEmptyInventoryFormData() {
  return {
    partNumber: '',
    referencePartNumber: '',
    aircraftScope: 'GEN',
    aircraftId: '',
    aircraftRegistration: 'GEN',
    description: '',
    category: 'PART',
    condition: 'SERVICEABLE',
    storePartCode: '',
    storeType: '',
    locationText: '',
    unitMetric: 'EACH',
    quantity: 0,
    location: { warehouse: '', shelf: '', bin: '' },
    expiryDate: '',
    shelfLifeNotApplicable: false,
    status: 'IN_INVENTORY',
    statusNotes: '',
    unitDetails: []
  };
}

/** Seed dialog form state from an inventory item document. */
export function seedFormDataFromInventoryItem(item) {
  if (!item) return createEmptyInventoryFormData();
  return {
    partNumber: item.partNumber || '',
    referencePartNumber: item.referencePartNumber || '',
    aircraftScope: item.aircraftScope || 'GEN',
    aircraftId: item.aircraftId?._id ? String(item.aircraftId._id) : (item.aircraftId ? String(item.aircraftId) : ''),
    aircraftRegistration: item.aircraftId?.registration || item.aircraftRegistration || 'GEN',
    description: item.description || '',
    category: normalizeInventoryCategory(item.category),
    condition: partConditionSelectValue(item.condition),
    storePartCode: item.storePartCode || '',
    storeType: item.storeType || '',
    locationText: item.locationText || '',
    unitMetric: item.unitMetric || 'EACH',
    quantity: item.quantity ?? 0,
    location: normalizeReceivingLocation(item.location),
    expiryDate: item.expiryDate ? toDateInputValue(item.expiryDate) : '',
    shelfLifeNotApplicable: item.shelfLifeNotApplicable || false,
    status: item.status || 'IN_INVENTORY',
    statusNotes: '',
    unitDetails: withLineStoreTypeOnUnits(
      Array.isArray(item.unitDetails) ? item.unitDetails.map((unit) => ({
        ...unit,
        expiryDate: normalizeUnitExpiryForForm(unit?.expiryDate, item.shelfLifeNotApplicable)
      })) : [],
      item.storeType
    )
  };
}

/** Seed dialog form state from a receiving grid line. */
export function seedFormDataFromReceivingLine(line, headerAircraft = {}) {
  const base = createEmptyInventoryFormData();
  if (!line) return base;
  const headerScope = headerAircraft.scope || 'GEN';
  const headerId = headerAircraft.id ? String(headerAircraft.id) : '';
  const headerReg = headerAircraft.registration || 'GEN';
  const shelfLifeNotApplicable = Boolean(line.shelfLifeNotApplicable);
  return {
    ...base,
    partNumber: line.partNumber || '',
    referencePartNumber: line.referencePartNumber || '',
    aircraftScope: line.aircraftScope || headerScope,
    aircraftId: line.aircraftId?._id
      ? String(line.aircraftId._id)
      : (line.aircraftId != null && typeof line.aircraftId !== 'object'
        ? String(line.aircraftId)
        : (headerScope === 'AIRCRAFT' ? headerId : '')),
    aircraftRegistration: line.aircraftId?.registration
      || line.aircraftRegistration
      || (headerScope === 'AIRCRAFT' ? headerReg : 'GEN'),
    description: line.description || '',
    category: normalizeInventoryCategory(line.category),
    condition: partConditionSelectValue(line.condition),
    storePartCode: line.storePartCode || '',
    storeType: normalizeStoreTypeValue(line.storeType) || '',
    locationText: line.locationText || '',
    unitMetric: line.unit || 'EACH',
    quantity: Number(line.receivedQuantity) || Number(line.quantity) || 0,
    location: normalizeReceivingLocation(line.location),
    expiryDate: shelfLifeNotApplicable ? '' : (line.shelfLifeUpto ? toDateInputValue(line.shelfLifeUpto) : ''),
    shelfLifeNotApplicable,
    unitDetails: withLineStoreTypeOnUnits(
      Array.isArray(line.unitDetails) ? line.unitDetails.map((unit) => ({
        ...unit,
        expiryDate: normalizeUnitExpiryForForm(unit?.expiryDate, shelfLifeNotApplicable)
      })) : [],
      line.storeType
    ),
    certificateNumber: line.certificateNumber || '',
    batchNumber: line.batchNumber || '',
    serialNumber: resolveLineSerialNumber(line),
    remarks: line.remarks || ''
  };
}

/** Merge dialog form values back into a receiving grid line. */
export function mergeFormDataIntoReceivingLine(formData, existingLine = {}, { selectedPoId = false } = {}) {
  const qty = Math.max(0, Number(formData.quantity) || 0);
  const unitDetails = Array.isArray(formData.unitDetails) ? formData.unitDetails : [];
  const serialNumbers = uniqueNonEmptyStrings(
    unitDetails.map((unit) => unit?.serialNumber)
  );
  const uniqueBatches = uniqueNonEmptyStrings(
    unitDetails.map((unit) => unit?.batchNumber)
  );
  const next = {
    ...existingLine,
    partNumber: String(formData.partNumber || '').trim(),
    referencePartNumber: String(formData.referencePartNumber || '').trim() || undefined,
    description: String(formData.description || '').trim(),
    category: normalizeInventoryCategory(formData.category),
    condition: partConditionSelectValue(formData.condition),
    storePartCode: String(formData.storePartCode || '').trim() || undefined,
    storeType: normalizeStoreTypeValue(formData.storeType) || undefined,
    locationText: String(formData.locationText || '').trim() || undefined,
    unit: formData.unitMetric || 'EACH',
    location: normalizeReceivingLocation(formData.location),
    shelfLifeNotApplicable: Boolean(formData.shelfLifeNotApplicable),
    shelfLifeUpto: formData.shelfLifeNotApplicable ? '' : (formData.expiryDate || ''),
    unitDetails: withLineStoreTypeOnUnits(unitDetails.map((unit) => ({
      ...unit,
      expiryDate: isShelfLifeNaValue(unit?.expiryDate)
        ? SHELF_LIFE_NA_VALUE
        : (unitExpiryForPersistence(unit?.expiryDate) || '')
    })), formData.storeType),
    aircraftScope: formData.aircraftScope || 'GEN',
    aircraftId: formData.aircraftScope === 'AIRCRAFT' ? (formData.aircraftId || undefined) : undefined,
    aircraftRegistration: formData.aircraftScope === 'GEN'
      ? 'GEN'
      : (formData.aircraftRegistration || '').toUpperCase(),
    receivedQuantity: qty,
    certificateNumber: formData.certificateNumber ?? existingLine.certificateNumber,
    remarks: formData.remarks ?? existingLine.remarks,
    serialNumbers: serialNumbers.length > 0 ? serialNumbers : existingLine.serialNumbers,
    batchNumber: uniqueBatches.length === 1
      ? uniqueBatches[0]
      : (formData.batchNumber || existingLine.batchNumber || ''),
    serialNumber: serialNumbers.length > 0
      ? formatLineSerialNumber(serialNumbers)
      : normalizeLineSerialNumberDisplay(
        formData.serialNumber || existingLine.serialNumber || ''
      )
  };
  if (!selectedPoId) {
    next.quantity = qty;
  }
  next.condition = deriveReceivingLineCondition(next);
  return next;
}

export function buildInventorySavePayload(formData) {
  const payload = { ...formData };
  if (payload.aircraftScope === 'GEN') {
    delete payload.aircraftId;
    payload.aircraftRegistration = 'GEN';
  } else if (!payload.aircraftId) {
    delete payload.aircraftId;
  }
  payload.shelfLifeNotApplicable = Boolean(payload.shelfLifeNotApplicable);
  if (payload.shelfLifeNotApplicable) payload.expiryDate = null;
  if (!payload.expiryDate) payload.expiryDate = null;
  if (payload.statusNotes !== undefined && payload.statusNotes === '') delete payload.statusNotes;
  if (Array.isArray(payload.unitDetails)) {
    payload.unitDetails = payload.unitDetails.map((unit) => {
      const row = { ...unit };
      const expiry = unitExpiryForPersistence(row.expiryDate);
      if (expiry) row.expiryDate = expiry;
      else delete row.expiryDate;
      return row;
    });
  }
  delete payload.certificateNumber;
  delete payload.batchNumber;
  delete payload.serialNumber;
  delete payload.remarks;
  delete payload.reorderPoint;
  delete payload.minimumStock;
  return payload;
}

export function inventorySaveErrorMessage(error) {
  const data = error?.response?.data;
  if (data?.message) return data.message;
  const firstValidation = data?.errors?.[0];
  if (firstValidation?.msg) {
    const path = firstValidation.path ? `${firstValidation.path}: ` : '';
    return `${path}${firstValidation.msg}`;
  }
  return 'Error saving inventory item';
}
